import React from 'react';

import type {IWaterRightsSubStepProps} from '../farmInformation.types';

export const WaterRightsSubStep: React.FC<IWaterRightsSubStepProps> = ({
  waterRightsError,
  isLoadingWaterRights,
  isSavingWaterRights,
  hasWaterRightsStepValue,
  setHasWaterRightsStepValue,
  selectedWaterSource,
  selectedHouseholdSource,
  waterRightForm,
  setWaterRightForm,
  waterRightsRecords,
  onWaterRightFormChange,
  onAddWaterRightRecord,
  onRemoveWaterRightRecord,
  yesNoOptions,
  sourceOptions,
  scaleOptions,
}) => (
  <div className="application-card">
    <div className="subform-stack">
      <header className="subform-stack__header">
        <h3>Water rights</h3>
        <p>
          Capture your water rights records exactly as they appear on your
          permits.
        </p>
      </header>

      {waterRightsError ? (
        <div className="form-alert is-error">{waterRightsError}</div>
      ) : null}

      {isLoadingWaterRights ? (
        <div className="application-empty-state">
          <h3>Loading water rights</h3>
        </div>
      ) : null}

      <fieldset className="money-choice-group">
        <legend className="money-choice-group__legend">
          Do you have water rights?
        </legend>
        <div className="money-choice-group__options money-choice-group__options--inline">
          {yesNoOptions.map((option) => {
            const checked =
              (hasWaterRightsStepValue ? 'yes' : 'no') === option.value;

            return (
              <label
                key={option.value}
                className={`money-choice money-choice--radio${
                  checked ? ' is-checked' : ''
                }`}
              >
                <input
                  type="radio"
                  className="money-choice__control"
                  name="has-water-rights"
                  value={option.value}
                  checked={checked}
                  onChange={() =>
                    setHasWaterRightsStepValue(option.value === 'yes')
                  }
                />
                <span className="money-choice__label-wrap">
                  <span className="money-choice__label">{option.label}</span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {hasWaterRightsStepValue ? (
        <>
          <div className="form-grid two-columns water-rights-grid">
            <div className="money-form-field">
              <label className="money-form-field__label" htmlFor="water-source">
                Water source(s)
              </label>
              <select
                id="water-source"
                className="money-form-field__control money-form-field__control--select"
                value={selectedWaterSource}
                onChange={(event) =>
                  setWaterRightForm((prev) => ({
                    ...prev,
                    waterSource: event.target.value,
                  }))
                }
              >
                <option value="">Select water source</option>
                {sourceOptions.map((option) => (
                  <option
                    key={`water-source-${option.value}`}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="money-form-field">
              <label
                className="money-form-field__label"
                htmlFor="household-consumption"
              >
                Household consumption
              </label>
              <select
                id="household-consumption"
                className="money-form-field__control money-form-field__control--select"
                value={selectedHouseholdSource}
                onChange={(event) =>
                  setWaterRightForm((prev) => ({
                    ...prev,
                    householdConsumption: event.target.value,
                  }))
                }
              >
                <option value="">Select household source</option>
                {sourceOptions.map((option) => (
                  <option
                    key={`household-consumption-${option.value}`}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="money-form-field">
              <label className="money-form-field__label" htmlFor="water-scheme">
                Scheme
              </label>
              <input
                id="water-scheme"
                className="money-form-field__control money-form-field__control--input"
                value={waterRightForm.scheme}
                onChange={onWaterRightFormChange('scheme')}
              />
            </div>

            <div className="money-form-field">
              <label
                className="money-form-field__label"
                htmlFor="permit-number"
              >
                Permit number
              </label>
              <input
                id="permit-number"
                className="money-form-field__control money-form-field__control--input"
                value={waterRightForm.permitNumber}
                onChange={onWaterRightFormChange('permitNumber')}
              />
            </div>

            <div className="money-form-field">
              <label
                className="money-form-field__label"
                htmlFor="water-schedule"
              >
                Schedule
              </label>
              <select
                id="water-schedule"
                className="money-form-field__control money-form-field__control--select"
                value={waterRightForm.schedule}
                onChange={onWaterRightFormChange('schedule')}
              >
                <option value="">Select frequency</option>
                <option value="daily">daily</option>
                <option value="weekly">weekly</option>
                <option value="hourly">hourly</option>
              </select>
            </div>

            <div className="money-form-field">
              <label className="money-form-field__label" htmlFor="water-scale">
                Water scale
              </label>
              <select
                id="water-scale"
                className="money-form-field__control money-form-field__control--select"
                value={waterRightForm.waterScale}
                onChange={onWaterRightFormChange('waterScale')}
              >
                <option value="">Select water scale</option>
                {scaleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="money-form-field">
              <label
                className="money-form-field__label"
                htmlFor="water-quantity"
              >
                Water quantity
              </label>
              <input
                id="water-quantity"
                type="number"
                min="0"
                className="money-form-field__control money-form-field__control--input"
                value={waterRightForm.waterQuantity}
                onChange={onWaterRightFormChange('waterQuantity')}
              />
            </div>

            <fieldset className="money-choice-group">
              <legend className="money-choice-group__legend">
                Controlled water supply
              </legend>
              <div className="money-choice-group__options">
                {yesNoOptions.map((option) => {
                  const checked = waterRightForm.controlled === option.value;

                  return (
                    <label
                      key={`controlled-${option.value}`}
                      className={`money-choice money-choice--radio${
                        checked ? ' is-checked' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        className="money-choice__control"
                        name="water-controlled"
                        value={option.value}
                        checked={checked}
                        onChange={() =>
                          setWaterRightForm((prev) => ({
                            ...prev,
                            controlled: option.value === 'yes' ? 'yes' : 'no',
                          }))
                        }
                      />
                      <span className="money-choice__label-wrap">
                        <span className="money-choice__label">
                          {option.label}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          </div>

          <div className="money-form-field">
            <label className="money-form-field__label" htmlFor="water-notes">
              Notes
            </label>
            <textarea
              id="water-notes"
              rows={3}
              className="money-form-field__control money-form-field__control--textarea"
              value={waterRightForm.notes}
              onChange={onWaterRightFormChange('notes')}
            />
          </div>

          <div className="subform-stack__actions">
            <button
              type="button"
              className="money-btn money-btn--secondary money-btn--sm"
              onClick={onAddWaterRightRecord}
              disabled={isLoadingWaterRights || isSavingWaterRights}
            >
              Add water-right entry
            </button>
            <p>{waterRightsRecords.length} entries prepared</p>
          </div>

          {waterRightsRecords.length > 0 ? (
            <ul className="simple-record-list">
              {waterRightsRecords.map((record, index) => (
                <li key={`${record.id ?? 'draft'}-${index}`}>
                  <div>
                    <strong>{record.scheme || 'Unnamed scheme'}</strong>
                    <p>
                      Permit: {record.permitNumber || '-'} | Source:{' '}
                      {(record.waterSource || []).join(', ') || '-'}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="money-btn money-btn--danger money-btn--sm"
                    onClick={() => {
                      void onRemoveWaterRightRecord(record);
                    }}
                    disabled={isSavingWaterRights}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : (
        <p className="form-note">
          We will remove existing water-right records for this application when
          you continue.
        </p>
      )}
    </div>
  </div>
);
